import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { Coordinates, AccommodationListingDTO } from '@apps/accommodations/AccommodationsConsole.types';
import { SkeletonText } from '@shared/components/Skeleton';
import { StarRating, UserRating } from './Ratings';
import { TravelLogistics } from './TravelLogistics';

interface HeaderBlockProps {
  listing: AccommodationListingDTO;
  venueCoordinates: Coordinates | null;
  isMobileOrTablet: boolean;
  redirectUrl?: string;
}

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', md: 'row' }}
      columnGap={7}
      justifyContent="space-between"
      rowGap={3}
    >
      {children}
    </Flex>
  );
};

const LeftPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={4}
    >
      {children}
    </Flex>
  );
};

const NameAndStarRatingPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      gap={5}
      flexWrap={'wrap'}
      alignItems={'center'}
    >
      {children}
    </Flex>
  );
};

const HeaderBlock = ({ listing, venueCoordinates, isMobileOrTablet, redirectUrl }: HeaderBlockProps) => {
  const addressText = useMemo(() => {
    if (listing.address) {
      return [listing.address.street, listing.address.city].filter(Boolean).join(', ');
    }

    return null;
  }, [listing]);
  const isSoldOut = listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates;

  return (
    <Container>
      {!isMobileOrTablet && (
        <LeftPanel>
          {typeof listing.starRating === 'number' && listing.starRating > 0 && <StarRating rating={listing.starRating} />}
          <TextV2
            typographyVariant="hed4"
            _hover={{ textDecoration: !isSoldOut && redirectUrl ? 'underline' : 'none' }}
            maxWidth={260}
          >
            {listing.name}
          </TextV2>
          {listing.address && <TextV2 typographyVariant="label2">{addressText}</TextV2>}
        </LeftPanel>
      )}
      {isMobileOrTablet && (
        <NameAndStarRatingPanel>
          <TextV2
            typographyVariant="hed4"
            maxWidth={260}
          >
            {listing.name}
          </TextV2>
          {typeof listing.starRating === 'number' && listing.starRating > 0 && <StarRating rating={listing.starRating} />}
        </NameAndStarRatingPanel>
      )}
      {isMobileOrTablet && (
        <TravelLogistics
          useShortWording={true}
          venueCoordinates={venueCoordinates}
          listing={listing}
        />
      )}
      {listing.userRating && (
        <UserRating
          rating={listing.userRating.ratingValue}
          reviewCount={listing.userRating.ratingCount}
        />
      )}
    </Container>
  );
};

HeaderBlock.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  return (
    <Container>
      {!isMobileOrTablet && (
        <LeftPanel>
          <StarRating.Skeleton />
          <Flex
            flexDirection="column"
            rowGap={3}
          >
            <SkeletonText
              rows={1}
              skeletonHeight={20}
              width={175}
            />
            <SkeletonText
              rows={1}
              skeletonHeight={20}
              width={80}
            />
          </Flex>
          <SkeletonText
            rows={1}
            skeletonHeight={18}
            width={160}
          />
        </LeftPanel>
      )}
      {isMobileOrTablet && (
        <NameAndStarRatingPanel>
          <SkeletonText
            rows={1}
            skeletonHeight={20}
            width={175}
          />
          <StarRating.Skeleton />
        </NameAndStarRatingPanel>
      )}
      {isMobileOrTablet && <TravelLogistics.Skeleton />}
      <UserRating.Skeleton />
    </Container>
  );
};

export { HeaderBlock };
